import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useUpdateMentorship } from "@/lib/api/mentorships.ts";
import { AxiosError } from "axios";
import type { ReactNode } from "react";
import { useState } from "react";

interface UpdateMentorshipProps {
  mentorshipId: string;
  userId: string;
  deactivated: boolean;
  disabled?: boolean;
  trigger?: ReactNode | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function UpdateMentorship({
  mentorshipId,
  userId,
  deactivated,
  disabled,
  trigger,
  open: openProp,
  onOpenChange,
}: UpdateMentorshipProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const { mutateAsync: updateMentorship, isPending } =
    useUpdateMentorship(userId);

  const handleUpdate = async () => {
    await updateMentorship({
      mentorshipId,
      deactivated: !deactivated,
    });
  };

  const defaultTrigger = (
    <Button variant="secondary" disabled={disabled}>
      {deactivated ? "Reactivate" : "Deactivate"} mentorship
    </Button>
  );
  const triggerNode =
    openProp !== undefined ? (trigger ?? null) : (trigger ?? defaultTrigger);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {triggerNode ? <DialogTrigger asChild>{triggerNode}</DialogTrigger> : null}
      <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="mb-1 text-2xl font-semibold">
              {deactivated
                ? "Do you want to reactivate the mentorship?"
                : "Do you want to deactivate the mentorship?"}
            </DialogTitle>
            <DialogDescription className="text-base">
              {deactivated
                ? "This will reactivate the mentorship."
                : "This will deactivate the mentorship."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-row items-center gap-4">
            <Button
              className="w-full text-lg font-bold text-slate-900"
              variant="outline"
              onClick={setOpen.bind(null, false)}
              disabled={isPending}
            >
              No
            </Button>
            <Button
              className="w-full text-lg font-bold text-white"
              variant="default"
              onClick={async () => {
                try {
                  await handleUpdate();
                  setOpen(false);
                } catch (error) {
                  // Toast handled in useUpdateMentorship; keep dialog open on 409.
                  if (
                    error instanceof AxiosError &&
                    error.response?.status === 409
                  ) {
                    return;
                  }
                  throw error;
                }
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
